import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Grid,
  Stack,
  Typography,
  Avatar,
  Switch,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  Search,
  Shield,
  FolderShared,
  Settings,
  ArrowForward,
  AdminPanelSettings,
  Visibility,
} from "@mui/icons-material";

const mapStaffToUser = (staff) => {
  if (!staff) return { id: "unassigned", name: "Unassigned", avatar: "U", email: "", department: "" };
  return {
    id: staff.Id || staff.id,
    name: staff.name || `${staff.FirstName || ""} ${staff.LastName || ""}`.trim(),
    email: staff.email || "",
    avatar: staff.avatar || `${(staff.FirstName || "?")[0]}${(staff.LastName || "?")[0]}`.toUpperCase(),
    department: staff.department || "General",
    image: staff.image || null,
  };
};

export default function TeamView({
  staffsList = [],
  boardPermissions = [], // Outgoing permissions: [{ shared_with_id, permission }]
  incomingPermissions = [], // Incoming permissions: [{ owner_id, permission, hasAccess }]
  currentUser,
  onGrantAccess, // Callback: (userId, hasAccess, permission) => void
  onGoToBoard, // Callback: (userId) => void
}) {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter out the current user from the list of members we can grant access to
  const otherStaffs = staffsList.filter(
    (s) => s.Id !== currentUser.id && s.id !== currentUser.id
  );

  const filteredStaffs = otherStaffs.filter((s) => {
    const fullName = `${s.FirstName || ""} ${s.LastName || ""}`.toLowerCase();
    const dept = (s.department || "").toLowerCase();
    const email = (s.email || "").toLowerCase();
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || dept.includes(query) || email.includes(query);
  });

  return (
    <Box sx={{ flex: 1, overflowY: "auto", p: 3, bgcolor: "#F8FAFC" }}>
      <Box sx={{ maxWidth: "1280px", mx: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
        
        {/* Title */}
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1A1A24">
            Team Collaboration & Board Permissions
          </Typography>
          <Typography fontSize={13} color="#717182" mt={0.5}>
            Configure who can view and edit your personal task board, and access boards shared with you.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Outgoing Permissions List (Left Pane) */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: "12px", boxShadow: "none" }}>
              <CardHeader
                title={
                  <Typography fontSize={16} fontWeight={600} color="#1A1A24">
                    Manage Access to Your Task Board
                  </Typography>
                }
                subheader={
                  <Typography fontSize={12} color="#717182" mt={0.5}>
                    Grant viewing or editing privileges to other team members.
                  </Typography>
                }
                sx={{ pb: 2 }}
              />
              <Divider />
              <CardContent sx={{ p: 3 }}>
                {/* Search Bar */}
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search team members by name, department, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      fontSize: 13,
                      bgcolor: "white",
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                      "&:hover fieldset": { borderColor: "#6C5CE7" },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: "#717182" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Staff list */}
                <Stack gap={2}>
                  {filteredStaffs.map((staff) => {
                    const u = mapStaffToUser(staff);
                    
                    // Check if this user has access to our board
                    const permissionRecord = boardPermissions.find(
                      (p) => p.shared_with_id === u.id
                    );
                    const hasAccess = !!permissionRecord;
                    const permissionLevel = permissionRecord?.permission || "view";

                    return (
                      <Box
                        key={u.id}
                        sx={{
                          p: 2,
                          bgcolor: "white",
                          borderRadius: "10px",
                          border: "1px solid rgba(0,0,0,0.05)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: { xs: "wrap", sm: "nowrap" },
                          gap: 2,
                          transition: "all 0.15s ease",
                          "&:hover": {
                            bgcolor: "#F8FAFC",
                            borderColor: "rgba(108,92,231,0.2)",
                          },
                        }}
                      >
                        <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
                          <Avatar
                            src={u.image ? `https://mindsinaction.com.na/assets/media/pictures/${u.image}` : undefined}
                            sx={{ width: 42, height: 42, bgcolor: "#6C5CE7", color: "white", fontSize: 16 }}
                          >
                            {u.avatar}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontSize={14} fontWeight={600} color="#1A1A24" noWrap>
                              {u.name}
                            </Typography>
                            <Typography fontSize={11.5} color="#717182" noWrap>
                              {u.email}
                            </Typography>
                            <Chip
                              label={u.department}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                mt: 0.5,
                                bgcolor: "#F1F5F9",
                                color: "#475569",
                                borderRadius: "4px",
                              }}
                            />
                          </Box>
                        </Stack>

                        <Stack direction="row" alignItems="center" gap={3} sx={{ width: { xs: "100%", sm: "auto" }, justifyContent: "space-between" }}>
                          {/* Access Switch */}
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Typography fontSize={12} fontWeight={500} color={hasAccess ? "#6C5CE7" : "#717182"}>
                              {hasAccess ? "Access Granted" : "No Access"}
                            </Typography>
                            <Switch
                              checked={hasAccess}
                              onChange={(e) => onGrantAccess(u.id, e.target.checked, permissionLevel)}
                              size="small"
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: "#6C5CE7",
                                  "& + .MuiSwitch-track": { backgroundColor: "#6C5CE7" },
                                },
                              }}
                            />
                          </Stack>

                          {/* Permission Select */}
                          {hasAccess && (
                            <FormControl size="small" sx={{ width: 120 }}>
                              <Select
                                value={permissionLevel}
                                onChange={(e) => onGrantAccess(u.id, true, e.target.value)}
                                sx={{
                                  fontSize: 12,
                                  height: 32,
                                  bgcolor: "white",
                                  borderRadius: "6px",
                                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.08)" },
                                }}
                              >
                                <MenuItem value="view" sx={{ fontSize: 12 }}>Viewer</MenuItem>
                                <MenuItem value="edit" sx={{ fontSize: 12 }}>Editor</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}

                  {filteredStaffs.length === 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography fontSize={13} color="#717182">
                        No team members found matching your search.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Incoming Permissions Grid (Right Pane) */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: "12px", boxShadow: "none", height: "100%" }}>
              <CardHeader
                title={
                  <Typography fontSize={16} fontWeight={600} color="#1A1A24">
                    Task Boards Shared with You
                  </Typography>
                }
                subheader={
                  <Typography fontSize={12} color="#717182" mt={0.5}>
                    Other team members' boards you have permission to view or edit.
                  </Typography>
                }
                sx={{ pb: 2 }}
              />
              <Divider />
              <CardContent sx={{ p: 3 }}>
                <Stack gap={2}>
                  {incomingPermissions.map((permission) => {
                    const owner = staffsList.find(
                      (s) => s.Id === permission.owner_id || s.id === permission.owner_id
                    );
                    if (!owner) return null;
                    const u = mapStaffToUser(owner);
                    const isEditor = permission.permission === "edit";

                    return (
                      <Box
                        key={u.id}
                        sx={{
                          p: 2,
                          bgcolor: "#F8FAFC",
                          borderRadius: "10px",
                          border: "1px solid rgba(0,0,0,0.04)",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        <Stack direction="row" alignItems="center" gap={1.5}>
                          <Avatar
                            src={u.image ? `https://mindsinaction.com.na/assets/media/pictures/${u.image}` : undefined}
                            sx={{ width: 36, height: 36, bgcolor: "#6C5CE7", color: "white", fontSize: 14 }}
                          >
                            {u.avatar}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontSize={13.5} fontWeight={600} color="#1A1A24" noWrap>
                              {u.name}'s Board
                            </Typography>
                            <Typography fontSize={11} color="#717182" noWrap>
                              {u.department}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.5}>
                          <Chip
                            icon={isEditor ? <AdminPanelSettings sx={{ fontSize: "14px !important" }} /> : <Visibility sx={{ fontSize: "14px !important" }} />}
                            label={isEditor ? "Editor (Full Access)" : "Viewer (Read-only)"}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: 11,
                              bgcolor: isEditor ? "#E6F4EA" : "#E8F0FE",
                              color: isEditor ? "#10B981" : "#3B82F6",
                              fontWeight: 600,
                              "& .MuiChip-icon": { color: "inherit" },
                            }}
                          />
                          <Button
                            variant="text"
                            size="small"
                            endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                            onClick={() => onGoToBoard(u.id)}
                            sx={{
                              textTransform: "none",
                              fontSize: 12.5,
                              color: "#6C5CE7",
                              fontWeight: 600,
                              p: 0,
                              minWidth: "unset",
                              "&:hover": { bgcolor: "transparent", color: "#5a4aa3" },
                            }}
                          >
                            Go to Board
                          </Button>
                        </Stack>
                      </Box>
                    );
                  })}

                  {incomingPermissions.length === 0 && (
                    <Box
                      sx={{
                        p: 4,
                        textAlign: "center",
                        bgcolor: "#F8FAFC",
                        borderRadius: "10px",
                        border: "1px dashed rgba(0,0,0,0.1)",
                      }}
                    >
                      <FolderShared sx={{ fontSize: 32, color: "#94A3B8", mb: 1 }} />
                      <Typography fontSize={13} color="#717182" fontWeight={500}>
                        No boards shared with you yet.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
